import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';

import PageHeader from '../../components/PageHeader';

// GraphQL Query
const GET_PIPEDRIVE_ACTIVITIES = gql`
  query GetPipedriveActivities($noOfMinutes: Int, $companyId: Int, $actionType: String) {
    getPipedriveActivities(noOfMinutes: $noOfMinutes, companyId: $companyId, actionType: $actionType) {
      id
      actionType
      companyId
      companyDomain
      pipedriveId
      status
      errorMessage
      details
      timestamp
      datetimeCreated
    }
  }
`;

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  padding: theme.spacing(2),
  borderRadius: '12px 12px 0 0',
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const StatusChip = styled(Chip)<{ status?: string }>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return {
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          color: theme.palette.success.dark,
          borderColor: theme.palette.success.main,
        };
      case 'failed':
        return {
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          color: theme.palette.error.dark,
          borderColor: theme.palette.error.main,
        };
      case 'partial':
        return {
          backgroundColor: alpha(theme.palette.warning.main, 0.1),
          color: theme.palette.warning.dark,
          borderColor: theme.palette.warning.main,
        };
      default:
        return {
          backgroundColor: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
          borderColor: theme.palette.grey[400],
        };
    }
  };

  return {
    ...getStatusColor(),
    borderWidth: 1,
    borderStyle: 'solid',
    fontWeight: 500,
  };
});

const ExpandMoreButton = styled(IconButton)<{ expand: boolean }>(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

// Helper functions
const formatActionType = (actionType: string) => {
  return actionType
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatTimeAgo = (timestamp: number) => {
  const seconds = Math.floor(Date.now() / 1000 - timestamp);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
};

const formatDomain = (domain: string) => {
  if (!domain) return 'Unknown';
  return domain
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'success':
      return <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />;
    case 'failed':
      return <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />;
    case 'partial':
      return <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
    default:
      return null;
  }
};

// Activity item component
const ActivityItem = ({ activity }: { activity: any }) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 1,
        backgroundColor: alpha('#F6ECE1', 0.3),
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          {getStatusIcon(activity.status)}
          <Box>
            <Typography variant="body1" fontWeight={500}>
              {formatActionType(activity.actionType)}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              {activity.pipedriveId && <Chip size="small" label={`PD: ${activity.pipedriveId}`} variant="outlined" />}
              <StatusChip size="small" label={activity.status} status={activity.status} />
              <Typography variant="caption" color="text.secondary">
                <ScheduleIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                {formatTimeAgo(activity.timestamp)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {(activity.details || activity.errorMessage) && (
          <ExpandMoreButton
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
            size="small"
          >
            <ExpandMoreIcon />
          </ExpandMoreButton>
        )}
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box mt={2} pl={4}>
          {activity.errorMessage && (
            <Typography variant="body2" color="error" gutterBottom>
              <strong>Error:</strong> {activity.errorMessage}
            </Typography>
          )}
          {activity.details && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Details:</strong>
              </Typography>
              <pre
                style={{
                  fontSize: '12px',
                  backgroundColor: alpha('#3A4248', 0.05),
                  padding: '8px',
                  borderRadius: '4px',
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(activity.details, null, 2)}
              </pre>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Company card component
const CompanyActivityCard = ({ domain, activities }: { domain: string; activities: any[] }) => {
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const successCount = activities.filter(a => a.status === 'success').length;
  const failedCount = activities.filter(a => a.status === 'failed').length;
  const partialCount = activities.filter(a => a.status === 'partial').length;

  return (
    <StyledCard>
      <HeaderBox>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600} color="primary.dark">
                {formatDomain(domain)}
              </Typography>
              {activities[0]?.companyId && (
                <Typography variant="caption" color="text.secondary">
                  Company ID: {activities[0].companyId}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <Stack direction="row" spacing={1}>
              {successCount > 0 && (
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  label={`${successCount} Success`}
                  color="success"
                  variant="outlined"
                />
              )}
              {failedCount > 0 && (
                <Chip
                  size="small"
                  icon={<ErrorIcon />}
                  label={`${failedCount} Failed`}
                  color="error"
                  variant="outlined"
                />
              )}
              {partialCount > 0 && (
                <Chip
                  size="small"
                  icon={<WarningIcon />}
                  label={`${partialCount} Partial`}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
            <ExpandMoreButton
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMoreButton>
          </Box>
        </Box>
      </HeaderBox>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {activities.map(activity => (
            <ActivityItem key={activity.id} activity={activity} />
          ))}
        </CardContent>
      </Collapse>
    </StyledCard>
  );
};

// Main component
const PipedriveActivityPage = () => {
  const [timeFilter, setTimeFilter] = useState(30); // Default to 30 minutes to reduce load
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [pollingInterval] = useState(120000); // 120 seconds (2 minutes) to reduce API calls

  const { loading, error, data, refetch } = useQuery(GET_PIPEDRIVE_ACTIVITIES, {
    variables: {
      noOfMinutes: timeFilter,
      actionType: actionTypeFilter || null,
      companyId: companyFilter ? parseInt(companyFilter) : null,
    },
    pollInterval: pollingInterval,
    fetchPolicy: 'cache-and-network', // Show cached data while fetching new
    errorPolicy: 'all', // Continue showing data even if there's an error
  });

  // Get all activities for extracting unique companies
  const allActivities = data?.getPipedriveActivities || [];

  // Extract unique companies for filter dropdown
  const uniqueCompanies = Array.from(
    new Map(
      allActivities
        .filter((activity: any) => activity.companyId && activity.companyDomain)
        .map((activity: any) => [
          activity.companyId,
          {
            id: activity.companyId,
            domain: activity.companyDomain || 'Unknown',
          },
        ])
    ).values()
  ).sort((a: any, b: any) => a.domain.localeCompare(b.domain));

  // Filter activities based on selected company
  const filteredActivities = companyFilter
    ? allActivities.filter((activity: any) => activity.companyId === parseInt(companyFilter))
    : allActivities;

  // Group filtered activities by company domain
  const groupedActivities =
    filteredActivities.reduce((acc: any, activity: any) => {
      const domain = activity.companyDomain || 'Unknown';
      if (!acc[domain]) {
        acc[domain] = [];
      }
      acc[domain].push(activity);
      return acc;
    }, {}) || {};

  // Sort domains by most recent activity
  const sortedDomains = Object.keys(groupedActivities).sort((a, b) => {
    const aLatest = Math.max(...groupedActivities[a].map((act: any) => act.timestamp));
    const bLatest = Math.max(...groupedActivities[b].map((act: any) => act.timestamp));
    return bLatest - aLatest;
  });

  const handleRefresh = () => {
    refetch();
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    // Only show error if we have no data at all
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <Typography variant="h6" color="error">
          Error loading Pipedrive activities
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {error.message.includes('ProvisionedThroughputExceededException')
            ? 'Too many requests. Try reducing the time range or wait a moment.'
            : error.message}
        </Typography>
        <Button variant="outlined" color="primary" onClick={() => refetch()} sx={{ mt: 2 }} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Pipedrive Activity"
        subtitle="Monitor all Pipedrive API operations and synchronization status"
        variant="default"
      />

      <Box sx={{ p: 3 }}>
        {error && data && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              bgcolor: alpha('#FF564F', 0.1),
              borderLeft: '4px solid #FF564F',
            }}
          >
            <Typography variant="body2" color="error">
              {error.message.includes('ProvisionedThroughputExceededException')
                ? 'Unable to fetch latest data due to rate limits. Showing cached results.'
                : `Error updating data: ${error.message}`}
            </Typography>
          </Paper>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              size="small"
              label="Time Range"
              value={timeFilter}
              onChange={e => setTimeFilter(Number(e.target.value))}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value={15}>Last 15 min</MenuItem>
              <MenuItem value={30}>Last 30 min</MenuItem>
              <MenuItem value={60}>Last 1 hour</MenuItem>
              <MenuItem value={120}>Last 2 hours</MenuItem>
              <MenuItem value={360}>Last 6 hours</MenuItem>
              <MenuItem value={1440}>Last 24 hours</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Company"
              value={companyFilter}
              onChange={e => setCompanyFilter(e.target.value)}
              sx={{ minWidth: 250 }}
              disabled={uniqueCompanies.length === 0}
            >
              <MenuItem value="">All Companies</MenuItem>
              {uniqueCompanies.map((company: any) => (
                <MenuItem key={company.id} value={company.id.toString()}>
                  {formatDomain(company.domain)} (ID: {company.id})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Action Type"
              value={actionTypeFilter}
              onChange={e => setActionTypeFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="create_organization">Create Organization</MenuItem>
              <MenuItem value="update_organization">Update Organization</MenuItem>
              <MenuItem value="create_deal_via_zapier">Create Deal</MenuItem>
              <MenuItem value="create_note">Create Note</MenuItem>
              <MenuItem value="search_organization">Search Organization</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </TextField>
          </Stack>

          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {sortedDomains.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh">
            <Typography variant="h6" color="text.secondary">
              No Pipedrive activities found
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Activities will appear here when Pipedrive operations are performed
            </Typography>
            <Typography variant="caption" color="text.secondary" mt={2} sx={{ fontStyle: 'italic' }}>
              Note: Activity logging is being optimized for performance
            </Typography>
          </Box>
        ) : (
          <Box>
            {sortedDomains.map(domain => (
              <CompanyActivityCard key={domain} domain={domain} activities={groupedActivities[domain]} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PipedriveActivityPage;
